import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../services/firebase";

export default function EditWine() {
  const { id } = useParams();
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: "",
    region: "",
    year: "",
    price: "",
    cost: "",
    barcode: "",
    description: "",
    imageUrl: "",
    category: "",
    type: "",
    trackStock: true,
    forSale: true,
    stock: 0,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError("ID invalide.");
        setLoading(false);
        return;
      }
      try {
        const d = await getDoc(doc(db, "winesek", id));
        if (!d.exists()) {
          setError("Vin introuvable.");
          setLoading(false);
          return;
        }
        const data = d.data() || {};

        setForm({
          name: data.name || "",
          region: data.region || "",
          year: data.year ?? "",
          price: data.price ?? "",
          cost: data.cost ?? "",
          barcode: data.barcode || "",
          description: data.description || "",
          imageUrl: data.imageUrl || "",
          category: data.category || "",
          type: data.type || "",
          trackStock:
            typeof data.trackStock === "boolean" ? data.trackStock : true,
          forSale:
            typeof data.forSale === "boolean" ? data.forSale : true,
          stock:
            typeof data.stock === "number" ? data.stock : 0,
        });
      } catch (err) {
        console.error(err);
        setError("Erreur lors du chargement.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Nom requis.";
    if (form.year && !/^\d{4}$/.test(String(form.year)))
      return "Année invalide.";
    if (form.price && isNaN(Number(form.price))) return "Prix invalide.";
    if (form.cost && isNaN(Number(form.cost))) return "Coût invalide.";
    if (
      form.stock !== "" &&
      (!Number.isFinite(Number(form.stock)) || Number(form.stock) < 0)
    )
      return "Stock invalide.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) return setError(v);
    setSaving(true);
    setError("");
    try {
      const ref = doc(db, "winesek", id);
      await updateDoc(ref, {
        name: form.name,
        nameLower: (form.name || "").toLowerCase(),
        region: form.region || "",
        year: form.year ? Number(form.year) : null,
        price: form.price ? Number(form.price) : null,
        cost: form.cost ? Number(form.cost) : null,
        barcode: form.barcode || "",
        description: form.description || "",
        imageUrl: form.imageUrl || "",
        category: form.category || "",
        type: form.type || "",
        trackStock: !!form.trackStock,
        forSale: !!form.forSale,
        stock: Number(form.stock) || 0,
        updatedAt: new Date(),
      });

      // adapte cette route à ton vrai détail produit si besoin
      nav(`/product/${id}`);
    } catch (err) {
      console.error(err);
      setError("Impossible de sauvegarder.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="container py-4">Chargement…</div>;
  if (error)
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );

  return (
    <div className="container py-4">
      <h2>Éditer le vin</h2>
      <form onSubmit={handleSubmit} className="mt-3">
        <div className="row g-3">
          <div className="col-md-6 form-floating">
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="form-control"
              placeholder="Nom"
              required
            />
            <label>Nom *</label>
          </div>

          <div className="col-md-6 form-floating">
            <input
              name="region"
              value={form.region}
              onChange={handleChange}
              className="form-control"
              placeholder="Région"
              required
            />
            <label>Région *</label>
          </div>

          <div className="col-6 col-md-3 form-floating">
            <input
              name="year"
              value={form.year || ""}
              onChange={handleChange}
              className="form-control"
              placeholder="Année"
              maxLength={4}
            />
            <label>Année</label>
          </div>

          <div className="col-6 col-md-3 form-floating">
            <input
              name="price"
              value={form.price || ""}
              onChange={handleChange}
              type="number"
              className="form-control"
              placeholder="Prix (FCFA)"
              step="1"
              min="0"
            />
            <label>Prix (FCFA)</label>
          </div>

          <div className="col-6 col-md-3 form-floating">
            <input
              name="cost"
              value={form.cost || ""}
              onChange={handleChange}
              type="number"
              className="form-control"
              placeholder="Coût (FCFA)"
              step="1"
              min="0"
            />
            <label>Coût (FCFA)</label>
          </div>

          <div className="col-6 col-md-3 form-floating">
            <input
              name="barcode"
              value={form.barcode || ""}
              onChange={handleChange}
              className="form-control"
              placeholder="Code-barres / UPC"
            />
            <label>Code-barres / UPC</label>
          </div>

          <div className="col-md-6 form-floating">
            <input
              name="imageUrl"
              value={form.imageUrl}
              onChange={handleChange}
              className="form-control"
              placeholder="Image (URL)"
            />
            <label>Image (URL)</label>
          </div>

          <div className="col-12 form-floating">
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              className="form-control"
              placeholder="Description"
              style={{ minHeight: 120 }}
            />
            <label>Description</label>
          </div>

          <div className="col-6 col-md-3">
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="forSale"
                name="forSale"
                checked={form.forSale}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="forSale">
                À vendre
              </label>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="trackStock"
                name="trackStock"
                checked={form.trackStock}
                onChange={handleChange}
              />
            <label className="form-check-label" htmlFor="trackStock">
                Suivre le stock
              </label>
            </div>
          </div>

          <div className="col-6 col-md-3 form-floating">
            <input
              name="stock"
              value={form.stock}
              onChange={handleChange}
              type="number"
              className="form-control"
              placeholder="Stock"
              min="0"
            />
            <label>Stock</label>
          </div>

          <div className="col-12 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => nav(-1)}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
